import { getLastNBufferedEvents, clearBuffer, addEventToBuffer } from './analyticsBuffer';
import { readCommonConfig } from '../commons/config';
import { IUserTokens } from '../currentuser/currentUser';
import { getActivityAnalyticsApi } from './activityAnalyticsApi';
import { AnalyticsEvent } from './AnalyticsEvent';
import axios, { AxiosInstance } from 'axios';

const BATCH_INTERVAL = 10_000; // 10 seconds

const createAnalyticsAxiosInstance = (tokens?: IUserTokens | null): AxiosInstance | null => {
  const userTokens = tokens || JSON.parse(localStorage.getItem('userTokens') || 'null');
  if (!userTokens?.access) {
    return null;
  }

  const { backendApiUrl } = readCommonConfig();
  return axios.create({
    baseURL: `${backendApiUrl}/v1`,
    headers: {
      Authorization: `Bearer ${userTokens.access}`,
      'Content-Type': 'application/json',
    },
  });
};

let intervalId: NodeJS.Timeout | null = null;

export const sendAnalyticsEventDirectly = async (
  analyticsEvent: AnalyticsEvent,
  properties: object = {},
  tokens?: IUserTokens | null
): Promise<boolean> => {
  const axiosInstance = createAnalyticsAxiosInstance(tokens);
  const eventName = analyticsEvent.name;
  if (!axiosInstance) {
    logger.info(`No auth token available, buffering event.: ${tokens}`);
    addEventToBuffer(eventName, properties);
    return false;
  }

  try {
    const activityAnalyticsApi = getActivityAnalyticsApi(axiosInstance);
    logger.info(`sendAnalyticsEventDirectly ${eventName}`);

    await activityAnalyticsApi.trackEvent(eventName, {
      ...properties,
      timestamp: new Date(),
    });
    return true;
  } catch (error) {
    console.warn('Failed to send analytics event directly:', error);
    addEventToBuffer(eventName, properties);
    return false;
  }
};

const sendEvents = async () => {
  const buffer = getLastNBufferedEvents(20);
  if (buffer.length === 0) {
    return;
  }

  const axiosInstance = createAnalyticsAxiosInstance();
  if (!axiosInstance) {
    return;
  }

  const activityAnalyticsApi = getActivityAnalyticsApi(axiosInstance);

  try {
    const eventName = buffer.map(({ eventName }) => `${eventName}`).join(', ');
    const properties = {
      events: buffer.map(({ eventName, properties, timestamp }) => ({
        eventName,
        properties,
        timestamp,
      })),
    };

    console.info(`Sending analytics events: ${eventName}`, properties);
    await activityAnalyticsApi.trackEvent(eventName, properties);
    clearBuffer();
  } catch (error) {
    console.warn('Failed to send analytics events:', error);
  }
};

export const startAnalyticsSender = () => {
  if (intervalId) {
    console.warn('Analytics sender is already running. Stopping the previous instance.');
    clearInterval(intervalId);
  }
  console.info('Starting analytics sender with interval:', BATCH_INTERVAL);
  intervalId = setInterval(sendEvents, BATCH_INTERVAL);
};

export const stopAnalyticsSender = () => {
  if (intervalId) {
    console.info('Stopping analytics sender.');
    clearInterval(intervalId);
    intervalId = null;
  }
};
